import { Sparkle, Unplugged } from './icons'
import { useLink } from './link/useLink'
import { PairingScreen } from './private/PairingScreen'
import { PrivatePortal } from './private/PrivatePortal'
import './console.css'

/**
 * The private portal, the console surface that asks for no account.
 *
 * It used to share this file with the review suite, and before that they shared one page with a
 * switch in the header. The switch was tying together the one boundary the product has: the
 * private portal holds data that never touched a server, and the admin panel is the shared
 * library with an account behind it.
 *
 * The panel has since grown its own full-screen chrome and lives in `admin/AdminPanel.tsx`, so
 * only the private portal is left here. `#phone` is this page. `#review` is the panel, reached
 * from the members' site by a reviewer, and neither page offers a way to the other.
 */

function ConsoleBar({ badge, strap }: { badge: string; strap: string }) {
  return (
    <header className="console-bar">
      <div className="console-identity">
        <span className="console-sigil">
          <Sparkle size={19} />
        </span>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h1 className="console-wordmark">Covellah</h1>
            <span className="console-badge">{badge}</span>
          </div>
          <span className="console-strap">{strap}</span>
        </div>
      </div>
    </header>
  )
}

/**
 * The private portal. Her phone, read on a larger screen.
 *
 * No account, on purpose: it reads nothing from the server, so asking her to identify herself to
 * the server in order to look at data the server will never receive would be worse privacy for
 * no gain.
 */
export function PrivateConsole() {
  const { status, servedByPhone, submitCode, loadFile, reset } = useLink()

  return (
    <div className="console">
      <ConsoleBar badge="Private portal" strap="Your phone, read on this screen and kept nowhere" />

      <div className="privacy-strip">
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Unplugged size={15} />
          <strong>Nothing on this page reaches a server.</strong>
          Your phone hands it straight to this tab, and closing the tab is the delete button.
        </span>
        {status.kind === 'loaded' ? (
          <span className="privacy-strip-right">
            <span>{payloadDeviceLine(status.payload.deviceLabel, status.via)}</span>
            <button type="button" className="cbtn cbtn-quiet" onClick={reset}>
              Disconnect
            </button>
          </span>
        ) : null}
      </div>

      {status.kind === 'loaded' ? (
        <PrivatePortal payload={status.payload} via={status.via} />
      ) : (
        <PairingScreen
          status={status}
          servedByPhone={servedByPhone}
          onCode={(code) => void submitCode(code)}
          onFile={(file, passphrase) => void loadFile(file, passphrase)}
        />
      )}
    </div>
  )
}

function payloadDeviceLine(label: string | undefined, via: 'local' | 'file'): string {
  if (via === 'file') return 'Read from a file'
  return label ? `Served by ${label}` : 'Served by your phone'
}
